import { Component, inject, OnInit, ChangeDetectorRef } from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { AdministrationService } from "../../../services/administration.service";
import { ListaCorreo, User, UserPhoto } from "../../../models/User";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatButtonModule } from "@angular/material/button";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  ScnackbarData,
  ToastService,
  SnackbarStyles,
} from "../../../commons/toast.service";
import {
  ImageCropperComponent,
  ImageCroppedEvent,
  LoadedImage,
} from "ngx-image-cropper";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { MatIconModule } from "@angular/material/icon";
import { MatCardModule } from "@angular/material/card";
import { Router } from "@angular/router";
import { AppTitleService } from "../../../services/app-title.service";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { forkJoin, catchError, of } from "rxjs";

@Component({
  selector: "app-datos-personales",
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatToolbarModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    ImageCropperComponent,
    MatIconModule,
    MatCheckboxModule,
  ],
  templateUrl: "./datos-personales.component.html",
  styleUrl: "./datos-personales.component.scss",
})
export class DatosPersonalesComponent implements OnInit {
  userDataFormControl = new FormGroup({
    id: new FormControl(0, [Validators.required]),
    ciruc: new FormControl("", [Validators.required]),
    nombres: new FormControl("", [Validators.required]),
    apellidos: new FormControl("", [Validators.required]),
    correo: new FormControl("", [Validators.required]),
    cargo: new FormControl("", [Validators.required]),
    titulo: new FormControl("", [Validators.required]),
    genero: new FormControl("", [Validators.required]),
    estadoCivil: new FormControl("", [Validators.required]),
    notificarme: new FormControl(false),
  });

  currentUser: User | undefined;
  currentUserPhoto: UserPhoto | undefined;
  currentListaCorreo: ListaCorreo | null = null;
  photoUrl: SafeUrl | null = null;

  fetchingUpdateUser = false;

  constructor(
    private administrationService: AdministrationService,
    private router: Router,
    private toast: ToastService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    inject(AppTitleService).setTitle("Administración");
  }

  ngOnInit(): void {
    forkJoin({
      userData: this.administrationService.GetCurrentUserData().pipe(
        catchError(err => { console.error("Error al cargar datos del usuario:", err); return of(null); })
      ),
      userPhoto: this.administrationService.GetCurrentUserPhoto().pipe(
        catchError(err => { console.error("Error al cargar foto:", err); return of(null); })
      ),
      notificacion: this.administrationService.GetNotificarme().pipe(
        catchError(err => { console.error("Error al cargar notificación:", err); return of(null); })
      )
    }).subscribe(results => {
      if (results.userData) {
        this.currentUser = results.userData;
        this.initializeUserDataFormFields();
      }
      if (results.userPhoto?.foto) {
        this.currentUserPhoto = results.userPhoto;
        const base64Image = 'data:image/png;base64,' + results.userPhoto.foto;
        this.photoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base64Image);
        this.croppedImage = base64Image;
      }
      if (results.notificacion && results.notificacion.existe) {
        this.userDataFormControl.controls.notificarme.setValue(results.notificacion.estado === true);
        this.currentListaCorreo = results.notificacion as any;
      } else {
        this.userDataFormControl.controls.notificarme.setValue(false);
      }
      this.cdr.detectChanges();
    });
  }

  private base64ToBlob(b64: string, contentType: string): Blob {
    const byteChars = atob(b64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
  }

  initializeUserDataFormFields() {
    this.userDataFormControl.controls.id.setValue(this.currentUser!.id);
    this.userDataFormControl.controls.ciruc.setValue(this.currentUser!.ciruc);
    this.userDataFormControl.controls.nombres.setValue(this.currentUser!.nombres);
    this.userDataFormControl.controls.apellidos.setValue(this.currentUser!.apellidos);
    this.userDataFormControl.controls.correo.setValue(this.currentUser!.correo);
    this.userDataFormControl.controls.cargo.setValue(this.currentUser!.cargo);
    this.userDataFormControl.controls.titulo.setValue(this.currentUser!.titulo);
    this.userDataFormControl.controls.genero.setValue(this.currentUser!.genero.toString());
    this.userDataFormControl.controls.estadoCivil.setValue(this.currentUser!.estadocivil.toString());
  }

  onSubmitUserDataForm() {
    let snackbarData: ScnackbarData = {};
    this.fetchingUpdateUser = true;
    const formControls = this.userDataFormControl.controls;
    const user: User = {
      id: formControls.id.value!,
      ciruc: formControls.ciruc.value!,
      nombres: formControls.nombres.value!,
      apellidos: formControls.apellidos.value!,
      correo: formControls.correo.value!,
      cargo: formControls.cargo.value!,
      titulo: formControls.titulo.value!,
      genero: parseInt(formControls.genero.value!),
      estadocivil: parseInt(formControls.estadoCivil.value!),
    };

    // Actualizar datos de usuario y preferencia de notificaciones
    this.administrationService.UpdateCurrentUserData(user).subscribe({
      next: (response) => {
        this.currentUser = response;
        this.initializeUserDataFormFields();

        // Actualizar preferencia de notificaciones
        const notificarme = formControls.notificarme.value ?? false;
        const listaCorreoDto: Partial<ListaCorreo> = {
          descripcion: user.correo,
          estado: notificarme ? 1 : 0,
        };

        // Si no existe el registro, crear con POST; si existe, actualizar con PUT
        if (!this.currentListaCorreo || this.currentListaCorreo.id === null) {
          this.administrationService
            .CreateNotificarme(listaCorreoDto)
            .subscribe({
              next: () => {
                snackbarData.style = SnackbarStyles.Success;
                snackbarData.message = "Datos actualizados!";
                this.toast.show(snackbarData);
                this.fetchingUpdateUser = false;
                this.reloadCurrentRoute();
              },
              error: (err) => {
                if (err.status === 409) {
                  this.administrationService
                    .UpdateNotificarme(listaCorreoDto)
                    .subscribe({
                      next: () => {
                        snackbarData.style = SnackbarStyles.Success;
                        snackbarData.message = "Datos actualizados!";
                        this.toast.show(snackbarData);
                        this.fetchingUpdateUser = false;
                        this.reloadCurrentRoute();
                      },
                      error: () => {
                        snackbarData.style = SnackbarStyles.Warning;
                        snackbarData.message =
                          "Datos actualizados, pero hubo un error al guardar las preferencias de notificación";
                        this.toast.show(snackbarData);
                        this.fetchingUpdateUser = false;
                      },
                    });
                } else {
                  snackbarData.style = SnackbarStyles.Warning;
                  snackbarData.message =
                    "Datos actualizados, pero hubo un error al guardar las preferencias de notificación";
                  this.toast.show(snackbarData);
                  this.fetchingUpdateUser = false;
                }
              },
            });
        } else {
          this.administrationService
            .UpdateNotificarme(listaCorreoDto)
            .subscribe({
              next: () => {
                snackbarData.style = SnackbarStyles.Success;
                snackbarData.message = "Datos actualizados!";
                this.toast.show(snackbarData);
                this.fetchingUpdateUser = false;
                this.reloadCurrentRoute();
              },
              error: () => {
                snackbarData.style = SnackbarStyles.Warning;
                snackbarData.message =
                  "Datos actualizados, pero hubo un error al guardar las preferencias de notificación";
                this.toast.show(snackbarData);
                this.fetchingUpdateUser = false;
              },
            });
        }
      },
      error: () => {
        snackbarData.style = SnackbarStyles.Error;
        snackbarData.message = "Ocurrió un problema!";
        this.toast.show(snackbarData);
        this.fetchingUpdateUser = false;
      },
    });
  }

  imageChangedEvent: Event | null = null;
  croppedImage: SafeUrl | string = "";
  croppedImageBlob: Blob | undefined;

  triggerFileInput(): void {
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  fileChangeEvent(event: Event): void {
    this.imageChangedEvent = event;
  }
  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = this.sanitizer.bypassSecurityTrustUrl(event.objectUrl!);
    this.croppedImageBlob = event.blob!;
  }

  onSavePhoto() {
    let snackbarData: ScnackbarData = {};
    this.administrationService
      .PutCurrentUserPhoto(this.croppedImageBlob!)
      .subscribe({
        next: (response) => {
          this.currentUserPhoto = response;
          snackbarData.message = "Foto actualizada";
          snackbarData.style = SnackbarStyles.Success;
          this.reloadCurrentRoute();
          this.toast.show(snackbarData);
        },
      });
  }

  onQuitarPhoto() {
    let snackbarData: ScnackbarData = {};
    this.administrationService.quitarPhoto().subscribe({
      next: (response) => {
        snackbarData.message = "Foto actualizada";
        snackbarData.style = SnackbarStyles.Success;
        this.reloadCurrentRoute();
        this.toast.show(snackbarData);
      },
    });
  }

  reloadCurrentRoute() {
    window.location.reload();
  }

  onBack() {
    this.router.navigate(["/administracion"]);
  }
}
